import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { fetchActiveGuestCampaign } from "../../services/shopifyCampaigns";
import styles from "./styles.module.css";

const CAMPAIGN_REFRESH_INTERVAL = 30000;

const CampaignPopup = () => {
  const [campaign, setCampaign] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const isDismissedRef = useRef(false);

  useEffect(() => {
    let isMounted = true;
    let latestRequestId = 0;

    const syncCampaign = async () => {
      const requestId = ++latestRequestId;
      const activeCampaign = await fetchActiveGuestCampaign();

      if (isMounted && requestId === latestRequestId) {
        setCampaign(activeCampaign);
        setIsOpen(Boolean(activeCampaign) && !isDismissedRef.current);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        syncCampaign();
      }
    };

    syncCampaign();
    const refreshTimer = window.setInterval(syncCampaign, CAMPAIGN_REFRESH_INTERVAL);
    window.addEventListener("focus", syncCampaign);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      isMounted = false;
      window.clearInterval(refreshTimer);
      window.removeEventListener("focus", syncCampaign);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    document.documentElement.classList.add("campaign-popup-open");
    document.body.classList.add("campaign-popup-open");

    return () => {
      document.documentElement.classList.remove("campaign-popup-open");
      document.body.classList.remove("campaign-popup-open");
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && campaign && (
        <motion.div
          className={styles.overlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <motion.div
            className={styles.popup}
            initial={{ scale: 0.92, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.92, opacity: 0 }}
            transition={{ type: "spring", damping: 24, stiffness: 220 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="campaign-popup-title"
            aria-describedby={campaign.description ? "campaign-popup-description" : undefined}
          >
            <button
              type="button"
              className={styles.closeButton}
              onClick={() => {
                isDismissedRef.current = true;
                setIsOpen(false);
              }}
              aria-label="Close campaign popup"
            >
              &times;
            </button>

            <h2 id="campaign-popup-title" className={`${styles.title} f-bold`}>
              {campaign.title}
            </h2>

            {campaign.description && (
              <p id="campaign-popup-description" className={styles.description}>
                {campaign.description}
              </p>
            )}

            {campaign.image?.url && (
              <img
                className={styles.campaignImage}
                src={campaign.image.url}
                alt={campaign.image.altText || campaign.title}
                width={campaign.image.width || undefined}
                height={campaign.image.height || undefined}
              />
            )}

            {campaign.buttonUrl && campaign.buttonTitle && (
              <a
                className={`${styles.actionButton} f-bold`}
                href={campaign.buttonUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {campaign.buttonTitle}
              </a>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CampaignPopup;
